import type { Analysis, FormValues } from '@/lib/types';
import type { SopPhaseGoals } from '@/lib/posture-analysis/rule-params';

export type OverallDisplay = {
  lateralizationLabel: 'RLat' | 'LLat' | 'Mixed' | 'Balanced';
  phaseLabel: 'Phase 1' | 'Phase 2' | 'Phase 3';
  patternLabel?: 'SB' | 'AO';
  fullText: string;
};

export type SectionSummaries = {
  cervical: {
    car: string;
    clf: string;
    summary: 'Left' | 'Right' | 'Mixed' | 'N/A';
    intact: boolean;
    dir: 'Left' | 'Right' | 'Both';
    hyper: boolean;
  };
  ribcage: {
    summary: 'Left' | 'Right' | 'Mixed' | 'N/A';
    delta: number | null; // magnitude difference driving ribcage bias
    intact: boolean;
    dir: 'Left' | 'Right' | 'Both';
    hyper: boolean;
  };
  armsTorso: {
    leftArm: 'IR bias' | 'ER bias' | 'Mixed' | 'N/A';
    torso: string; // descriptive torso bias
    rightArm: 'IR bias' | 'ER bias' | 'Mixed' | 'N/A';
    leftArmDelta: number | null; // |ER-IR| for left
    rightArmDelta: number | null; // |ER-IR| for right
  };
  pelvis: {
    summary: 'Left' | 'Right' | 'Mixed' | 'N/A';
    details: string;
    intact: boolean;
    dir: 'Left' | 'Right' | 'Both';
    hyper: boolean;
  };
  legs: {
    firBias: 'Left' | 'Right' | 'Mixed' | 'N/A';
    ferBias: 'Left' | 'Right' | 'Mixed' | 'N/A';
  };
  legsPerSide: {
    leftLeg: 'IR bias' | 'ER bias' | 'Mixed' | 'N/A';
    rightLeg: 'IR bias' | 'ER bias' | 'Mixed' | 'N/A';
    leftLegDelta: number | null; // |ER-IR| for left leg
    rightLegDelta: number | null; // |ER-IR| for right leg
    leftIntact: boolean;
    rightIntact: boolean;
    leftHyper: boolean;
    rightHyper: boolean;
  };
  footGround: {
    footPresentation: string;
    grounding: string;
    leftScore: number | null;
    rightScore: number | null;
    leftSupinated: boolean | null; // true supinated -> inward, false pronated -> outward, null unknown
    rightSupinated: boolean | null;
  };
};

function parseNum(v: any): number | null {
  if (v === null || v === undefined || v === '' || v === 'Select...') return null;
  const m = String(v).match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

function withinGoal(val: number | null, goal: number): boolean {
  if (val === null) return false;
  return val >= goal * 0.99; // within 1% of goal lower bound
}

const Goals = {
  CAR: 30,
  CLF: 30,
  ShoulderIR: 70,
  ShoulderER: 90,
  ShoulderABD: 45,
  PelvicRotation: 70, // Phase 2 lower bound
  SLR: 90,
  HipAbduction: 45,
  FemoralIR: 40,
  FemoralER: 45,
} as const;

export function computeOverallDisplay(
  formValues: FormValues | undefined,
  analysis: Analysis | null,
  clientPatternCode: string | undefined,
  phaseCode: 'P1' | 'P2' | 'P3' = 'P1',
  opts?: { suppressBalanced?: boolean }
): OverallDisplay {
  let lateralizationLabel: OverallDisplay['lateralizationLabel'] = 'Mixed';
  if (analysis) {
    const left = analysis.left?.length || 0;
    const right = analysis.right?.length || 0;
    const intact = analysis.intact?.length || 0;
    if (!opts?.suppressBalanced && intact >= left + right && intact > 0) lateralizationLabel = 'Balanced';
    else if (right > left) lateralizationLabel = 'RLat';
    else if (left > right) lateralizationLabel = 'LLat';
    else lateralizationLabel = 'Mixed';
  }

  let patternLabel: OverallDisplay['patternLabel'];
  if (clientPatternCode) {
    const first = clientPatternCode.split(',')[0]?.trim().toUpperCase();
    if (first.startsWith('SB')) patternLabel = 'SB';
    else if (first.startsWith('APT') || first.startsWith('AO')) patternLabel = 'AO';
  }

  const phaseLabel: OverallDisplay['phaseLabel'] = phaseCode === 'P3' ? 'Phase 3' : phaseCode === 'P2' ? 'Phase 2' : 'Phase 1';

  const parts: string[] = [];
  if (patternLabel) parts.push(patternLabel);
  parts.push(lateralizationLabel);
  parts.push(phaseLabel);

  return {
    lateralizationLabel,
    phaseLabel,
    patternLabel,
    fullText: parts.join(' ')
  };
}

export function computeSectionSummaries(formValues: FormValues, goals?: Partial<SopPhaseGoals>): SectionSummaries {
  const L = (key: string) => parseNum(formValues[`L_${key}`]);
  const R = (key: string) => parseNum(formValues[`R_${key}`]);

  // Cervical
  const carL = L('CervicalAxialRotation');
  const carR = R('CervicalAxialRotation');
  const clfL = L('CervicalLateralFlexion');
  const clfR = R('CervicalLateralFlexion');
  const carDisp = carL !== null && carR !== null ? `${carL} ${carL < (carR as number) ? '<' : carL > (carR as number) ? '>' : '='} ${carR}` : 'N/A';
  const clfDisp = clfL !== null && clfR !== null ? `${clfL} ${clfL < (clfR as number) ? '<' : clfL > (clfR as number) ? '>' : '='} ${clfR}` : 'N/A';
  let cervSummary: 'Left' | 'Right' | 'Mixed' | 'N/A' = 'N/A';
  if (carL !== null && carR !== null && clfL !== null && clfR !== null) {
    const leftWins = (carL > carR ? 1 : 0) + (clfL > clfR ? 1 : 0);
    const rightWins = (carR > carL ? 1 : 0) + (clfR > clfL ? 1 : 0);
    cervSummary = leftWins > rightWins ? 'Left' : rightWins > leftWins ? 'Right' : 'Mixed';
  }
  const gCAR = goals?.cervicalRotationMinDeg ?? Goals.CAR;
  const gCLF = goals?.cervicalLatFlexMinDeg ?? Goals.CLF;
  const cervicalIntact = (
    withinGoal(carL, gCAR) && withinGoal(carR, gCAR) &&
    withinGoal(clfL, gCLF) && withinGoal(clfR, gCLF)
  );
  const cervicalDir: 'Left' | 'Right' | 'Both' = cervicalIntact
    ? 'Both'
    // For CAR: arrow points toward lateralization; in RLP examples L< R yet arrow points Left → inverse of bigger value
    : (carL !== null && carR !== null
        ? (carR > carL ? 'Left' : carL > carR ? 'Right' : 'Both')
        : 'Both');
  const cervicalHyper = (
    (carL !== null && carL >= gCAR * 1.2) || (carR !== null && carR >= gCAR * 1.2) ||
    (clfL !== null && clfL >= gCLF * 1.2) || (clfR !== null && clfR >= gCLF * 1.2)
  );

  // Arms/Torso
  const lIR = L('ShoulderIR');
  const rIR = R('ShoulderIR');
  const lER = L('ShoulderER');
  const rER = R('ShoulderER');
  const lABD = L('ShoulderAbduction');
  const rABD = R('ShoulderAbduction');

  const leftArm = lIR !== null && lER !== null ? (lIR > lER ? 'IR bias' : (lER > lIR ? 'ER bias' : 'Mixed')) : 'N/A';
  const rightArm = rIR !== null && rER !== null ? (rIR > rER ? 'IR bias' : (rER > rIR ? 'ER bias' : 'Mixed')) : 'N/A';
  const torso = (rER !== null && lIR !== null && rABD !== null && lABD !== null)
    ? ((rER + rABD) > (lIR + lABD) ? 'R posterior expansion' : (lIR + lABD) > (rER + rABD) ? 'L anterior expansion' : 'Mixed')
    : 'N/A';
  const leftArmDelta = lIR !== null && lER !== null ? Math.abs(lER - lIR) : null;
  const rightArmDelta = rIR !== null && rER !== null ? Math.abs(rER - rIR) : null;

  // Ribcage lateralization derived from summed upper torso signals (proxy):
  let ribcageSummary: 'Left' | 'Right' | 'Mixed' | 'N/A' = 'N/A';
  let ribcageDelta: number | null = null;
  if (rER !== null && rABD !== null && lIR !== null && lABD !== null) {
    const rightSum = rER + rABD;
    const leftSum = lIR + lABD;
    const diff = rightSum - leftSum; // + favors Right
    ribcageDelta = Math.abs(diff);
    ribcageSummary = diff > 0 ? 'Right' : diff < 0 ? 'Left' : 'Mixed';
  }
  const gIR = goals?.shoulderIRMinDeg ?? Goals.ShoulderIR;
  const gER = Goals.ShoulderER; // fallback (not in RuleDocs schema yet)
  const gABD = goals?.shoulderAbdMinDeg ?? Goals.ShoulderABD;
  const ribcageIntact = (
    withinGoal(lIR, gIR) && withinGoal(rIR, gIR) &&
    withinGoal(lER, gER) && withinGoal(rER, gER) &&
    withinGoal(lABD, gABD) && withinGoal(rABD, gABD)
  );
  const ribcageDir: 'Left' | 'Right' | 'Both' = ribcageIntact
    ? 'Both'
    : (lABD !== null && rABD !== null
        ? (rABD > lABD ? 'Right' : lABD > rABD ? 'Left' : 'Both')
        : 'Both');
  const ribcageHyper = (
    (lER !== null && lER >= gER * 1.2) || (rER !== null && rER >= gER * 1.2) ||
    (lABD !== null && lABD >= gABD * 1.2) || (rABD !== null && rABD >= gABD * 1.2)
  );

  // Pelvis
  const lPR = L('PelvicRotation');
  const rPR = R('PelvicRotation');
  const lSLR = L('StraightLegRaise');
  const rSLR = R('StraightLegRaise');
  const lO = parseNum(formValues['L_HipExtension']);
  const rO = parseNum(formValues['R_HipExtension']);
  const lHA = L('HipAbduction');
  const rHA = R('HipAbduction');

  let pelvisScore = 0; // + favors Right, - favors Left
  if (lPR !== null && rPR !== null) pelvisScore += rPR > lPR ? 1 : lPR > rPR ? -1 : 0;
  if (lSLR !== null && rSLR !== null) pelvisScore += rSLR > lSLR ? 1 : lSLR > rSLR ? -1 : 0;
  if (lO !== null && rO !== null) pelvisScore += (rO >= 0 ? 1 : 0) - (lO >= 0 ? 1 : 0); // HipExtension: pass = >= 0
  if (lHA !== null && rHA !== null) pelvisScore += lHA > rHA ? -1 : rHA > lHA ? 1 : 0; // HA favors Left when larger
  const pelvisSummary = pelvisScore > 0 ? 'Right' : pelvisScore < 0 ? 'Left' : 'Mixed';
  const pelvisDetails = `PR ${lPR ?? 'N/A'} vs ${rPR ?? 'N/A'}, SLR ${lSLR ?? 'N/A'} vs ${rSLR ?? 'N/A'}, HipExt ${lO ?? 'N/A'} vs ${rO ?? 'N/A'}, HA ${lHA ?? 'N/A'} vs ${rHA ?? 'N/A'}`;
  const gPR = goals?.pelvisRotationMinDeg ?? Goals.PelvicRotation;
  const gSLR = goals?.slrMinDeg ?? Goals.SLR;
  const gHA = goals?.hipAbductionMinDeg ?? Goals.HipAbduction;
  const pelvisIntact = (
    withinGoal(lPR, gPR) && withinGoal(rPR, gPR) &&
    withinGoal(lSLR, gSLR) && withinGoal(rSLR, gSLR) &&
    withinGoal(lHA, gHA) && withinGoal(rHA, gHA)
  );
  const pelvisDir: 'Left' | 'Right' | 'Both' = pelvisIntact
    ? 'Both'
    : (lPR !== null && rPR !== null
        ? (rPR > lPR ? 'Right' : lPR > rPR ? 'Left' : 'Both')
        : 'Both');
  const pelvisHyper = (
    (lPR !== null && lPR >= gPR * 1.2) || (rPR !== null && rPR >= gPR * 1.2) ||
    (lSLR !== null && lSLR >= gSLR * 1.2) || (rSLR !== null && rSLR >= gSLR * 1.2) ||
    (lHA !== null && lHA >= gHA * 1.2) || (rHA !== null && rHA >= gHA * 1.2)
  );

  // Legs (Femoral rotation bias overall)
  const lFIR = L('FemoralIR');
  const rFIR = R('FemoralIR');
  const lFER = L('FemoralER');
  const rFER = R('FemoralER');
  const firBias = (lFIR !== null && rFIR !== null) ? (lFIR > rFIR ? 'Left' : rFIR > lFIR ? 'Right' : 'Mixed') : 'N/A';
  const ferBias = (lFER !== null && rFER !== null) ? (lFER > rFER ? 'Left' : rFER > lFER ? 'Right' : 'Mixed') : 'N/A';
  const leftLegDelta = lFIR !== null && lFER !== null ? Math.abs(lFER - lFIR) : null;
  const rightLegDelta = rFIR !== null && rFER !== null ? Math.abs(rFER - rFIR) : null;

  // Legs per side (to display arrows like arms)
  const leftLeg = (lFIR !== null && lFER !== null) ? (lFIR > lFER ? 'IR bias' : (lFER > lFIR ? 'ER bias' : 'Mixed')) : 'N/A';
  const rightLeg = (rFIR !== null && rFER !== null) ? (rFIR > rFER ? 'IR bias' : (rFER > rFIR ? 'ER bias' : 'Mixed')) : 'N/A';
  const gFIR = Goals.FemoralIR; // fallback not yet in RuleDocs schema
  const gFER = Goals.FemoralER; // fallback not yet in RuleDocs schema
  const leftLegIntact = withinGoal(lFIR, gFIR) && withinGoal(lFER, gFER);
  const rightLegIntact = withinGoal(rFIR, gFIR) && withinGoal(rFER, gFER);
  const leftLegHyper = (lFIR !== null && lFIR >= gFIR * 1.2) || (lFER !== null && lFER >= gFER * 1.2);
  const rightLegHyper = (rFIR !== null && rFIR >= gFIR * 1.2) || (rFER !== null && rFER >= gFER * 1.2);

  // Foot & Grounding
  const footPresentation = String(formValues['FootPresentation'] || 'N/A');
  const lG = String(formValues['L_GroundingPerformance'] || 'N/A');
  const rG = String(formValues['R_GroundingPerformance'] || 'N/A');
  const lGScore = parseNum(lG);
  const rGScore = parseNum(rG);
  let grounding = 'N/A';
  if (lG !== 'N/A' || rG !== 'N/A') {
    if (lG !== 'N/A' && rG !== 'N/A') grounding = `${rG === lG ? 'Even' : (rG.length > lG.length ? 'R > L' : 'L > R')} (${lG} | ${rG})`;
    else grounding = lG !== 'N/A' ? `L: ${lG}` : `R: ${rG}`;
  }
  const leftSupinated = /sup/i.test(footPresentation) ? true : (/pro/i.test(footPresentation) ? false : null);
  const rightSupinated = /sup/i.test(footPresentation) ? false : (/pro/i.test(footPresentation) ? true : null);

  return {
    cervical: { car: carDisp, clf: clfDisp, summary: cervSummary, intact: cervicalIntact, dir: cervicalDir, hyper: cervicalHyper },
    ribcage: { summary: ribcageSummary, delta: ribcageDelta, intact: ribcageIntact, dir: ribcageDir, hyper: ribcageHyper },
    armsTorso: { leftArm, torso, rightArm, leftArmDelta, rightArmDelta },
    pelvis: { summary: pelvisSummary, details: pelvisDetails, intact: pelvisIntact, dir: pelvisDir, hyper: pelvisHyper },
    legs: { firBias, ferBias },
    legsPerSide: { leftLeg, rightLeg, leftLegDelta, rightLegDelta, leftIntact: leftLegIntact, rightIntact: rightLegIntact, leftHyper: leftLegHyper, rightHyper: rightLegHyper },
    footGround: { footPresentation, grounding, leftScore: lGScore, rightScore: rGScore, leftSupinated, rightSupinated }
  };
}

export function directionClass(dir: 'Left' | 'Right' | 'Mixed' | 'N/A') {
  if (dir === 'Left') return 'border-red-300 bg-red-50 text-red-700';
  if (dir === 'Right') return 'border-primary/30 bg-primary/10 text-primary';
  if (dir === 'Mixed') return 'border-yellow-300 bg-yellow-50 text-yellow-800';
  return 'border-muted text-muted-foreground';
}

export function biasToDir(bias: 'IR bias' | 'ER bias' | 'Mixed' | 'N/A', side: 'Left' | 'Right'): 'Left' | 'Right' | 'Mixed' | 'N/A' {
  if (bias === 'Mixed' || bias === 'N/A') return bias;
  if (side === 'Left') {
    // Spec: L Arm ER bias = < (Left), L Arm IR bias = > (Right)
    return bias === 'ER bias' ? 'Left' : 'Right';
  }
  // Spec: R Arm ER bias = > (Right), R Arm IR bias = < (Left)
  return bias === 'ER bias' ? 'Right' : 'Left';
}

export function formatDelta(delta: number | null): string {
  if (delta === null || Number.isNaN(delta)) return '';
  return `${Math.round(delta)}°`;
}


